import React, { useState, useEffect, useRef } from 'react';
import { Award, Shield, Heart } from 'lucide-react';
import { isMobile, isTablet } from '../../../shared/utils/deviceUtils';

const features = [
  {
    icon: Shield,
    title: 'Best Price Guarantee',
    description: 'We guarantee the best prices for all safari packages',
  },
  {
    icon: Award,
    title: 'Expert Local Guides',
    description: 'Professional guides with years of experience',
  },
  {
    icon: Heart,
    title: '24/7 Support',
    description: 'Round-the-clock customer support for your peace of mind',
  },
];

export const Features: React.FC = () => {
  const mobile = isMobile();
  const tablet = isTablet();
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [hasAnimated]);

  return (
    <section
      className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-background-secondary`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-4xl'} font-bold text-text-primary mb-4`}
          >
            Why Choose Us
          </h2>
          <p
            className={`${mobile ? 'text-base' : 'text-lg'} text-text-secondary`}
          >
            We provide the best safari experience with unmatched service
          </p>
        </div>

        <div
          ref={elementRef}
          className={`grid ${mobile ? 'grid-cols-1 gap-4' : tablet ? 'grid-cols-1 md:grid-cols-3 gap-6' : 'grid-cols-1 md:grid-cols-3 gap-8'} justify-items-start p-4 sm:p-6 lg:p-8 transition-all duration-1000 ease-out`}
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible
              ? 'translateY(0) scale(1)'
              : 'translateY(50px) scale(0.95)',
            transitionDelay: mobile ? '100ms' : '200ms',
            transitionDuration: mobile ? '600ms' : '1000ms',
          }}
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className={`text-left ${mobile ? 'p-4' : 'p-6'} bg-surface-primary rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 ${mobile ? 'w-full' : ''}`}
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-3 sm:gap-4 mb-2 sm:mb-3">
                    <div
                      className={`${mobile ? 'w-10 h-10' : 'w-12 h-12'} bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon
                        className={`${mobile ? 'w-5 h-5' : 'w-6 h-6'} text-primary-500`}
                      />
                    </div>
                    <h3
                      className={`${mobile ? 'text-sm font-bold' : 'font-bold'} text-text-primary`}
                    >
                      {feature.title}
                    </h3>
                  </div>
                  <p
                    className={`${mobile ? 'text-xs' : 'text-sm'} text-text-secondary leading-relaxed`}
                  >
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
